import React, { lazy } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import { useUI } from '../../contexts/UIContext';
import { AppViewRouterProps } from './types';

const ProjectDocumentsView = lazy(() => import('../ProjectDocumentsView'));
const CalendarView = lazy(() => import('../CalendarView'));
const TimelineView = lazy(() => import('../TimelineView'));
const CommunicationView = lazy(() => import('../CommunicationView'));

export const ProjectAuxViewsRouter: React.FC<AppViewRouterProps> = (props) => {
  const {
    isProjectEditor,
    deferredVisibleTasks,
    handleAddTask,
    handleEditTaskMemo,
    calendarShowAllTasks,
    timelineRef,
    globalAllAssignees,
    activeDragId,
    registerViewDragHandlers,
    handleReorderTask,
  } = props;

  const projectCtx = useProject();
  const {
    activeProject,
    currentProjectId,
    updateTask,
    updateTasksMultiple,
    deleteTask,
    projectMetadata,
    updateProjectMetadata,
    setProjectMetadata,
    allProjects,
    globalMetadata,
    activeWorkspace,
  } = projectCtx;
  const sortTasksByDate = props.sortTasksByDate ?? projectCtx.sortTasksByDate;

  const {
    viewMode,
    searchQuery,
    setSearchQuery,
    searchMode,
    searchMatchIds,
    searchResults,
    searchIndex,
    activeSearchMatchId,
    showArchived,
    showCompleted,
    setShowCompleted,
    calendarDate,
    setCalendarDate,
    calendarViewType,
    setCalendarViewType,
    setEditingTask
  } = useUI();

  return (
    <>
      {/* Project Documents View */}
      {viewMode === 'projectDocuments' && activeProject && (
        <ProjectDocumentsView 
          project={activeProject}
          onUpdateMetadata={(updates) => updateProjectMetadata(currentProjectId, updates)}
          onAddTask={handleAddTask}
          isReadOnly={!isProjectEditor}
        />
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <CalendarView 
          tasks={deferredVisibleTasks}
          onEditTask={handleEditTaskMemo}
          showCompleted={showCompleted}
          onToggleShowCompleted={() => setShowCompleted(!showCompleted)}
          currentDate={calendarDate}
          setCurrentDate={setCalendarDate}
          viewType={calendarViewType}
          setViewType={setCalendarViewType}
          showAllTasks={calendarShowAllTasks}
          activeSearchMatchId={activeSearchMatchId}
          searchMatchIds={searchMatchIds}
          searchQuery={searchQuery}
          searchMode={searchMode}
          filterMode="all"
          globalMetadata={globalMetadata}
        />
      )}

      {/* Project Contact View */}
      {viewMode === 'projectContact' && (
        <CommunicationView 
          showArchived={showArchived}
          showCompleted={showCompleted}
          projects={allProjects}
          projectId={currentProjectId}
          activeWorkspace={activeWorkspace}
          activeTab="board"
          onTabChange={() => {}}
          isReadOnly={!isProjectEditor}
          onTaskUpdate={(id, updates, projId) => {
            if (id === 'new') {
              const newTask = handleAddTask(updates.type || 'contact', undefined, currentProjectId, '', {
                ...updates,
                isInlineNew: true
              });
              if (newTask) setEditingTask(newTask);
            } else if (id === 'meta') {
              const { headerColor } = updates as any;
              setProjectMetadata({
                ...projectMetadata,
                communicationHeaderColor: headerColor
              });
            } else {
              updateTask(id, updates, projId);
            }
          }}
          onAddTask={handleAddTask}
          onTaskDelete={deleteTask}
          onTaskSelect={(task) => setEditingTask(task)}
          onTaskUpdateMultiple={updateTasksMultiple}
          metadata={projectMetadata}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchMatchIds={searchMatchIds}
          searchResults={searchResults}
          searchIndex={searchIndex}
          activeSearchMatchId={activeSearchMatchId}
          searchMode={searchMode}
          allAssignees={globalAllAssignees}
          activeDragId={activeDragId}
          onRegisterDragHandlers={registerViewDragHandlers}
          onTaskReorder={handleReorderTask}
        />
      )}

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <TimelineView 
          ref={timelineRef}
          tasks={deferredVisibleTasks}
          isReadOnly={!isProjectEditor}
          onEditTask={handleEditTaskMemo}
          showCompleted={showCompleted}
          onToggleShowCompleted={() => setShowCompleted(!showCompleted)}
          onSortTasks={sortTasksByDate}
          searchQuery={searchQuery}
          searchResults={searchResults}
          searchIndex={searchIndex}
          activeSearchMatchId={activeSearchMatchId}
          searchMode={searchMode}
          onAddTask={handleAddTask}
        />
      )}
    </>
  );
};

export default ProjectAuxViewsRouter;
